// Detects the built-in browsers that email and social apps open links in.
// Many of them (Facebook, Instagram, the iOS Gmail app's web view, …) block or
// mishandle the pop-up window PayPal checkout opens, so the PayPal spinner just
// sits there. The form uses this to steer those visitors to a real browser or
// to the "email me an invoice" fallback.
//
// User-agent sniffing is a heuristic: it can miss a new app, and it never
// blocks anything — it only changes which hints are shown.

// Apps that stamp their name into the user agent. Checked in order.
const KNOWN_APPS = [
  { name: 'Facebook',  test: /FBAN|FBAV|FB_IAB/i },
  { name: 'Instagram', test: /Instagram/i },
  { name: 'LinkedIn',  test: /LinkedInApp/i },
  { name: 'X',         test: /Twitter/i },
  { name: 'TikTok',    test: /musical_ly|BytedanceWebview|TikTok/i },
  { name: 'Snapchat',  test: /Snapchat/i },
  { name: 'Pinterest', test: /Pinterest/i },
  { name: 'Google',    test: /\bGSA\//i },
];

// Returns null for a normal browser, otherwise { app, os } where `app` is the
// app's name when the user agent reveals it (null when it only looks like a
// generic embedded web view) and `os` is 'ios' | 'android' | 'other'.
export function detectInAppBrowser(ua) {
  if (!ua) return null;

  const isIOS     = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const os        = isIOS ? 'ios' : isAndroid ? 'android' : 'other';

  const known = KNOWN_APPS.find(a => a.test.test(ua));
  if (known) return { app: known.name, os };

  // iOS: Safari and the other real browsers (Chrome = CriOS, Firefox = FxiOS,
  // Edge = EdgiOS) all include "Safari/" in the user agent; an app's embedded
  // web view (where the Gmail app opens links) leaves it out.
  if (isIOS && !/Safari\//i.test(ua)) return { app: null, os };

  // Android: embedded WebViews carry "; wv)". Chrome Custom Tabs — what the
  // Android Gmail app uses — are real Chrome and don't match.
  if (isAndroid && /;\s*wv\)/i.test(ua)) return { app: null, os };

  return null;
}

export function currentInAppBrowser() {
  if (typeof navigator === 'undefined') return null;
  return detectInAppBrowser(navigator.userAgent || '');
}

// Android lets a page hand itself to Chrome with an intent: URL. The fallback
// URL keeps the visitor on this page if Chrome isn't installed. Whether the
// host app honours the intent is up to that app, so the button is a best effort.
export function chromeIntentUrl(href) {
  const url = new URL(href);
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;` +
    `package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(href)};end`;
}

// Short label for the staff alert email, e.g. "Facebook app (android)".
export function describeBrowser(info) {
  if (!info) return 'Standard browser';
  return `${info.app ? `${info.app} app` : 'In-app browser'} (${info.os})`;
}
